import {
  FC,
  ForwardedRef,
  forwardRef,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes
} from 'react';

import { css } from '@mui/material';

import { styled } from '@mui/material/styles';

import { ButtonProps, StyledOutlined } from 'components/UIkit/atoms/Button/StyledButtons';
import { Text } from 'components/UIkit/atoms/Text';
import { withSpacing, WithSpacingProps } from 'components/UIkit/theme/spacing';

interface InternalProps extends ButtonProps {
  children: ReactNode;
  icon: ReactNode;
  subText: string;
  isActive?: boolean;
  forwardedRef?: ForwardedRef<HTMLButtonElement>;
  width?: string;
  hasBoxShadow?: boolean;
  alignStart?: boolean;
}

type ExternalProps = Omit<InternalProps, 'forwardedRef'> & WithSpacingProps;

const OutlinedIconSubtext: FC<InternalProps> = ({
  onClick,
  disabled,
  icon,
  subText,
  isActive = false,
  testHook,
  children,
  forwardedRef,
  width,
  hasBoxShadow = true,
  alignStart = false
}) => (
  <StyledContainer
    onClick={onClick}
    disabled={disabled}
    ref={forwardedRef}
    data-test-hook={testHook}
    isActive={isActive}
    width={width}
    hasBoxShadow={hasBoxShadow}
    alignStart={alignStart}
  >
    {icon}
    <Text variant="button-medium-outline">{children}</Text>
    <>&nbsp;</>
    <Text variant="button-medium-outline" color="secondary">
      {subText}
    </Text>
  </StyledContainer>
);

const StyledContainer = styled(StyledOutlined, {
  shouldForwardProp: (prop) => prop !== 'hasBoxShadow' && prop !== 'alignStart'
})<{ hasBoxShadow: boolean; alignStart: boolean }>(
  ({ theme, hasBoxShadow, alignStart }) => css`
    box-shadow: ${hasBoxShadow ? theme.boxShadow.common : 'none'};
    display: flex;
    align-items: center;
    justify-content: ${alignStart ? 'flex-start' : 'initial'};

    svg {
      margin-right: ${theme.spacing(8)};
      color: ${theme.palette.secondary.dark};
    }

    &:disabled {
      box-shadow: none;

      svg {
        color: ${theme.palette.text.disabled};
      }
    }
  `
);

const OutlinedIconSubtextWithSpacing = withSpacing(OutlinedIconSubtext);

export const OutlinedIconSubtextButton: FC<ExternalProps> = forwardRef<
  HTMLButtonElement,
  ExternalProps
>((props, ref) => (
  <OutlinedIconSubtextWithSpacing {...props} forwardedRef={ref} />
)) as ForwardRefExoticComponent<ExternalProps & RefAttributes<HTMLButtonElement>>;
